import React from 'react'
import { Document, Page, Text, View, StyleSheet } from '@react-pdf/renderer'

interface Debt {
  id: string
  name: string
  amount: number
  interestRate: number
  includeInMortgage: boolean
}

interface MortgageScenario {
  id: string
  name: string
  bankName: string
  comments: string
  propertyValue: number
  downPayment: number
  mortgageAmount: number
  interestRate: number
  termYears: number
  termMonths: number
  amortizationYears: number
  amortizationMonths: number
  paymentFrequency: string
  insured: boolean
  insuranceRate: number
  notaryFees: number
  cashback: number
  penalty: number
  debts: Debt[]
  monthlyPayment?: number
  totalCostOverTerm?: number
  totalInterestOverTerm?: number
  totalCostOverAmortization?: number
  totalInterestOverAmortization?: number
  totalPayments?: number
  debtInterestCostOverTerm?: number
  consolidatedDebtAmount?: number
  savingsFromConsolidation?: number
}

interface User {
  id: string
  email: string
  name: string
  firstName?: string
  lastName?: string
  role: 'admin' | 'broker' | 'collaborator'
  language?: string
}

interface MortgageCalculatorPDFProps {
  scenarios: MortgageScenario[]
  activeScenarios: string[]
  language?: 'en' | 'fr'
  broker?: User | null
  customerName?: string
  propertyAddress?: string
}

const styles = StyleSheet.create({
  page: {
    flexDirection: 'column',
    backgroundColor: '#FFFFFF',
    padding: 15,
    fontFamily: 'Helvetica',
    fontSize: 9
  },
  title: {
    fontSize: 18,
    fontWeight: 'bold',
    marginBottom: 15,
    textAlign: 'center',
    color: '#2563eb'
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 12
  },
  titleSection: {
    flex: 1
  },
  brokerInfo: {
    width: 200,
    padding: 10,
    backgroundColor: '#f8fafc',
    border: '1px solid #e2e8f0',
    borderRadius: 4
  },
  brokerTitle: {
    fontSize: 10,
    fontWeight: 'bold',
    color: '#374151',
    marginBottom: 5
  },
  brokerText: {
    fontSize: 8,
    color: '#111827',
    marginBottom: 2
  },
  scenariosContainer: {
    flexDirection: 'row',
    marginBottom: 10
  },
  scenarioColumn: {
    flex: 1,
    marginHorizontal: 6,
    padding: 8,
    backgroundColor: '#f8fafc',
    borderRadius: 4,
    border: '1px solid #e2e8f0'
  },
  scenarioTitle: {
    fontSize: 12,
    fontWeight: 'bold',
    marginBottom: 8,
    textAlign: 'center',
    color: '#1e40af',
    borderBottom: '1px solid #ddd',
    paddingBottom: 4
  },
  section: {
    marginBottom: 8
  },
  sectionTitle: {
    fontSize: 10,
    fontWeight: 'bold',
    marginBottom: 4,
    color: '#374151',
    backgroundColor: '#e5e7eb',
    padding: 2,
    borderRadius: 2
  },
  row: {
    flexDirection: 'row',
    marginBottom: 2,
    justifyContent: 'space-between'
  },
  label: {
    fontSize: 9,
    color: '#6b7280',
    flex: 1
  },
  value: {
    fontSize: 9,
    color: '#111827',
    fontWeight: 'bold',
    textAlign: 'right',
    flex: 1
  },
  negativeValue: {
    color: '#dc2626', // Red color for negative values like cashback
  },
  separatorLine: {
    borderTop: '1px solid #9ca3af',
    marginTop: 6,
    marginBottom: 6,
    width: '100%'
  },
  totalRow: {
    borderTop: '2px solid #374151',
    paddingTop: 4,
    marginTop: 2
  },
  totalLabel: {
    fontSize: 10,
    color: '#111827',
    flex: 1,
    fontWeight: 'bold'
  },
  totalValue: {
    fontSize: 10,
    color: '#111827',
    fontWeight: 'bold',
    textAlign: 'right',
    flex: 1
  },
  highlightedSection: {
    backgroundColor: '#dbeafe',
    border: '1px solid #3b82f6',
    borderRadius: 4,
    padding: 6,
    marginBottom: 6
  },
  highlightedTitle: {
    fontSize: 10,
    fontWeight: 'bold',
    color: '#1e40af',
    marginBottom: 3,
    textAlign: 'center'
  },
  savingsRow: {
    backgroundColor: '#dcfce7',
    border: '1px solid #16a34a',
    borderRadius: 3,
    padding: 4,
    marginTop: 4,
    marginBottom: 3,
    position: 'relative',
    zIndex: 100,
    overflow: 'hidden'
  },
  extraCostRow: {
    backgroundColor: '#fecaca',
    border: '1px solid #dc2626',
    borderRadius: 3,
    padding: 4,
    marginBottom: 4
  },
  savingsLabel: {
    fontSize: 9,
    color: '#15803d',
    fontWeight: 'bold',
    position: 'relative',
    zIndex: 101,
    backgroundColor: 'transparent'
  },
  savingsValue: {
    fontSize: 9,
    color: '#15803d',
    fontWeight: 'bold',
    position: 'relative',
    zIndex: 101,
    backgroundColor: 'transparent'
  },
  extraCostLabel: {
    fontSize: 9,
    color: '#dc2626',
    fontWeight: 'bold'
  },
  extraCostValue: {
    fontSize: 9,
    color: '#dc2626',
    fontWeight: 'bold'
  },
  debtSection: {
    marginTop: 4,
    padding: 4,
    backgroundColor: '#ffffff',
    border: '1px solid #d1d5db',
    borderRadius: 3
  },
  debtTitle: {
    fontSize: 9,
    fontWeight: 'bold',
    marginBottom: 3,
    color: '#374151'
  },
  debtItem: {
    marginBottom: 1,
    fontSize: 7,
    color: '#4b5563'
  },
  comparisonTable: {
    marginTop: 20,
    border: '1px solid #e5e7eb'
  },
  tableHeader: {
    flexDirection: 'row',
    backgroundColor: '#f3f4f6',
    borderBottom: '1px solid #e5e7eb'
  },
  tableRow: {
    flexDirection: 'row',
    borderBottom: '1px solid #f3f4f6'
  },
  tableHeaderCell: {
    flex: 1,
    padding: 8,
    borderRight: '1px solid #e5e7eb'
  },
  tableCell: {
    flex: 1,
    padding: 6,
    borderRight: '1px solid #f3f4f6'
  },
  tableHeaderText: {
    fontSize: 9,
    fontWeight: 'bold',
    color: '#374151',
    textAlign: 'center'
  },
  tableCellText: {
    fontSize: 8,
    color: '#111827',
    textAlign: 'center'
  },
  bestResultRow: {
    backgroundColor: '#dcfce7'
  },
  footer: {
    position: 'absolute',
    bottom: 15,
    left: 15,
    right: 15,
    textAlign: 'center',
    fontSize: 7,
    color: '#9ca3af',
    borderTop: '1px solid #e5e7eb',
    paddingTop: 5
  }
})

const translations = {
  en: {
    title: 'Mortgage Scenario Comparison',
    subtitle: 'Professional Mortgage Calculator Analysis',
    propertyDetails: 'Property Details',
    mortgageDetails: 'Mortgage Details',
    termResults: 'Term Results',
    existingDebts: 'Existing Debts',
    comparisonSummary: 'Comparison Summary',
    propertyValue: 'Property Value',
    downPayment: 'Down Payment',
    mortgageAmount: 'Mortgage Amount',
    interestRate: 'Interest Rate',
    termPeriod: 'Term Period',
    amortizationPeriod: 'Amortization Period',
    paymentFrequency: 'Payment Frequency',
    insured: 'Insured',
    insuranceRate: 'Insurance Rate',
    notaryFees: 'Notary Fees',
    cashback: 'Cashback',
    penalty: 'Penalty Fee',
    monthlyPayment: 'Monthly Payment',
    totalCostOverTerm: 'Total Cost Over Term',
    mortgageInterest: 'Mortgage Interest',
    separateDebtInterest: 'Separate Debt Interest',
    consolidationSavings: 'Consolidation Savings',
    savingsVsBest: 'This option saves you',
    interestForTerm: 'Interest for the Term',
    totalCostBreakdown: 'Total Cost Breakdown',
    consolidated: 'consolidated',
    separate: 'separate',
    years: 'years',
    year: 'year',
    months: 'months',
    yes: 'Yes',
    no: 'No',
    none: 'None',
    generatedAt: 'Generated on',
    page: 'Page',
    debtSummary: 'Debt Summary',
    noDebts: 'No debts',
    bankAndNotes: 'Bank & Notes',
    bank: 'Bank',
    notes: 'Notes',
    mortgageBroker: 'Mortgage Broker',
    disclaimer: 'Rates are not guaranteed and subject to change. This document is not a financing offer but an estimate of potential savings in the given scenario.'
  },
  fr: {
    title: 'Comparaison de Scénarios Hypothécaires',
    subtitle: 'Analyse Professionnelle de Calculateur Hypothécaire',
    propertyDetails: 'Détails de la Propriété',
    mortgageDetails: 'Détails Hypothécaires',
    termResults: 'Résultats du Terme',
    existingDebts: 'Dettes Existantes',
    comparisonSummary: 'Résumé de Comparaison',
    propertyValue: 'Valeur de la Propriété',
    downPayment: 'Mise de Fonds',
    mortgageAmount: 'Montant Hypothécaire',
    interestRate: 'Taux d\'Intérêt',
    termPeriod: 'Période du Terme',
    amortizationPeriod: 'Période d\'Amortissement',
    paymentFrequency: 'Fréquence de Paiement',
    insured: 'Assuré',
    insuranceRate: 'Taux d\'Assurance',
    notaryFees: 'Frais de Notaire',
    cashback: 'Remise en Argent',
    penalty: 'Frais de Pénalité',
    monthlyPayment: 'Paiement Mensuel',
    totalCostOverTerm: 'Coût Total sur le Terme',
    mortgageInterest: 'Intérêts Hypothécaires',
    separateDebtInterest: 'Intérêts Dettes Séparées',
    consolidationSavings: 'Économies de Consolidation',
    savingsVsBest: 'Cette option vous fait économiser',
    bankAndNotes: 'Banque et Notes',
    bank: 'Banque',
    notes: 'Notes',
    mortgageBroker: 'Courtier Hypothécaire',
    interestForTerm: 'Intérêts pour le Terme',
    totalCostBreakdown: 'Résumé des coûts pour le terme',
    consolidated: 'consolidées',
    separate: 'séparées',
    years: 'Ans',
    year: 'An', 
    months: 'mois',
    yes: 'Oui',
    no: 'Non',
    none: 'Aucune',
    generatedAt: 'Généré le',
    page: 'Page',
    debtSummary: 'Résumé des Dettes',
    noDebts: 'Aucune dette',
    disclaimer: 'Les taux ne sont pas garantis et peuvent changer à tout moment. Ce document n\'est pas une offre de financement mais une estimation des économies potentielles dans le scénario donné.'
  }
}

const MortgageCalculatorPDF: React.FC<MortgageCalculatorPDFProps> = ({ scenarios, activeScenarios, language = 'en', broker, customerName, propertyAddress }) => {
  const t = translations[language]
  const activeScenarioData = scenarios.filter(s => activeScenarios.includes(s.id))

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat(language === 'fr' ? 'fr-CA' : 'en-CA', {
      style: 'currency',
      currency: 'CAD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount)
  }

  const formatCurrencyWithSign = (amount: number) => {
    const formatted = formatCurrency(Math.abs(amount))
    return amount < 0 ? `-${formatted}` : formatted
  }

  const formatPercent = (rate: number) => {
    return `${rate.toFixed(2)}%`
  }

  const formatPeriod = (years: number, months: number) => {
    if (months === 0) {
      return years === 1 ? `${years} ${t.year || t.years}` : `${years} ${t.years}`
    }
    if (years === 0) return `${months} ${t.months}`
    const yearText = years === 1 ? (t.year || t.years) : t.years
    return `${years} ${yearText} ${months} ${t.months}`
  }

  const translatePaymentFrequency = (frequency: string, language: string) => {
    const translations: { [key: string]: { [key: string]: string } } = {
      'Monthly': { en: 'Monthly', fr: 'Mensuel' },
      'Bi-Weekly': { en: 'Bi-Weekly', fr: 'Aux 2 semaines' },
      'Weekly': { en: 'Weekly', fr: 'Hebdomadaire' },
      'Semi-Monthly': { en: 'Semi-Monthly', fr: 'Bi-mensuel' }
    }
    return translations[frequency]?.[language] || frequency
  }

  // Calculate savings compared to worst scenario (for cheapest option)
  const calculateSavings = (currentScenario: MortgageScenario) => {
    if (activeScenarioData.length < 2) return null
    const costs = activeScenarioData.map(s => s.totalCostOverTerm || 0)
    const minCost = Math.min(...costs)
    const maxCost = Math.max(...costs)
    const currentCost = currentScenario.totalCostOverTerm || 0
    // Only show savings if this is the cheapest option
    return currentCost === minCost ? maxCost - minCost : null
  }

  return (
    <Document>
      <Page size="A4" orientation="landscape" style={styles.page}>
        {/* Header with Title and Customer Info */}
        <View style={styles.header}>
          <View style={styles.titleSection}>
            <Text style={styles.title}>{t.title}</Text>
            {customerName && (
              <Text style={[styles.brokerText, { fontSize: 12, marginTop: 8 }]}>
                {language === 'fr' ? 'Client: ' : 'Customer: '}{customerName}
              </Text>
            )}
            {propertyAddress && (
              <Text style={[styles.brokerText, { fontSize: 11, marginTop: 4 }]}>
                {language === 'fr' ? 'Adresse de la propriété: ' : 'Property Address: '}{propertyAddress}
              </Text>
            )}
          </View>
          
          {broker && (
            <View style={styles.brokerInfo}>
              <Text style={styles.brokerTitle}>
                {t.mortgageBroker}
              </Text>
              <Text style={styles.brokerText}>
                {broker.firstName && broker.lastName ? 
                  `${broker.firstName} ${broker.lastName}` : 
                  broker.name}
              </Text>
            </View>
          )}
        </View>

        {/* Scenarios Side by Side */}
        <View style={styles.scenariosContainer}>
          {activeScenarioData.map(scenario => (
            <View key={scenario.id} style={styles.scenarioColumn}>
              <Text style={styles.scenarioTitle}>{scenario.name}</Text>

              {/* Bank & Comments */}
              {(scenario.bankName || scenario.comments) && (
                <View style={styles.section}>
                  <Text style={styles.sectionTitle}>{t.bankAndNotes}</Text>
                  {scenario.bankName && (
                    <View style={styles.row}>
                      <Text style={styles.label}>{t.bank}:</Text>
                      <Text style={styles.value}>{scenario.bankName}</Text>
                    </View>
                  )}
                  {scenario.comments && (
                    <View style={styles.row}>
                      <Text style={styles.label}>{t.notes}:</Text>
                      <Text style={[styles.value, { fontSize: 8 }]}>{scenario.comments}</Text>
                    </View>
                  )}
                </View>
              )}

              {/* Property Details */}
              <View style={styles.section}>
                <Text style={styles.sectionTitle}>{t.propertyDetails}</Text>
                <View style={styles.row}>
                  <Text style={styles.label}>{t.propertyValue}:</Text>
                  <Text style={styles.value}>{formatCurrency(scenario.propertyValue)}</Text>
                </View>
                <View style={styles.row}>
                  <Text style={styles.label}>{t.downPayment}:</Text>
                  <Text style={styles.value}>{formatCurrency(scenario.downPayment)}</Text>
                </View>
                <View style={styles.row}>
                  <Text style={styles.label}>{t.mortgageAmount}:</Text>
                  <Text style={styles.value}>{formatCurrency(scenario.mortgageAmount)}</Text>
                </View>
              </View>

              {/* Mortgage Details */}
              <View style={styles.section}>
                <Text style={styles.sectionTitle}>{t.mortgageDetails}</Text>
                <View style={styles.row}>
                  <Text style={styles.label}>{t.interestRate}:</Text>
                  <Text style={styles.value}>{formatPercent(scenario.interestRate)}</Text>
                </View>
                <View style={styles.row}>
                  <Text style={styles.label}>{t.termPeriod}:</Text>
                  <Text style={styles.value}>{formatPeriod(scenario.termYears, scenario.termMonths)}</Text>
                </View>
                <View style={styles.row}>
                  <Text style={styles.label}>{t.amortizationPeriod}:</Text>
                  <Text style={styles.value}>{formatPeriod(scenario.amortizationYears, scenario.amortizationMonths)}</Text>
                </View>
                <View style={styles.row}>
                  <Text style={styles.label}>{t.paymentFrequency}:</Text>
                  <Text style={styles.value}>{translatePaymentFrequency(scenario.paymentFrequency, language)}</Text>
                </View>
                <View style={styles.row}>
                  <Text style={styles.label}>{t.insured}:</Text>
                  <Text style={styles.value}>{scenario.insured ? t.yes : t.no}</Text>
                </View>
                {scenario.notaryFees > 0 && (
                  <View style={styles.row}>
                    <Text style={styles.label}>{t.notaryFees}:</Text>
                    <Text style={styles.value}>{formatCurrency(scenario.notaryFees)}</Text>
                  </View>
                )}
                {scenario.cashback !== 0 && (
                  <View style={styles.row}>
                    <Text style={styles.label}>{t.cashback}:</Text>
                    <Text style={styles.value}>{formatCurrencyWithSign(scenario.cashback)}</Text>
                  </View>
                )}
                {scenario.penalty > 0 && (
                  <View style={styles.row}>
                    <Text style={styles.label}>{t.penalty}:</Text>
                    <Text style={styles.value}>{formatCurrency(scenario.penalty)}</Text>
                  </View>
                )}
              </View>

              {/* Monthly Payment - Highlighted */}
              <View style={styles.highlightedSection}>
                <Text style={styles.highlightedTitle}>{t.monthlyPayment}</Text>
                <View style={styles.row}>
                  <Text style={styles.label}>{t.monthlyPayment}:</Text>
                  <Text style={styles.value}>{formatCurrency(scenario.monthlyPayment || 0)}</Text>
                </View>
              </View>

              {/* Total Cost Breakdown */}
              <View style={styles.highlightedSection}>
                <Text style={styles.highlightedTitle}>{t.totalCostBreakdown}</Text>
                <View style={styles.row}>
                  <Text style={styles.label}>{t.interestForTerm}:</Text>
                  <Text style={styles.value}>{formatCurrency((scenario.totalInterestOverTerm || 0) + (scenario.debtInterestCostOverTerm || 0))}</Text>
                </View>
                {scenario.notaryFees > 0 && (
                  <View style={styles.row}>
                    <Text style={styles.label}>{t.notaryFees}:</Text>
                    <Text style={styles.value}>{formatCurrency(scenario.notaryFees)}</Text>
                  </View>
                )}
                {scenario.cashback !== 0 && (
                  <View style={styles.row}>
                    <Text style={styles.label}>{t.cashback}:</Text>
                    <Text style={[styles.value, scenario.cashback < 0 ? styles.negativeValue : {}]}>
                      {formatCurrencyWithSign(scenario.cashback)}
                    </Text>
                  </View>
                )}
                {scenario.penalty > 0 && (
                  <View style={styles.row}>
                    <Text style={styles.label}>{t.penalty}:</Text>
                    <Text style={styles.value}>{formatCurrency(scenario.penalty)}</Text>
                  </View>
                )}
                
                {/* Separator line before total */}
                <View style={styles.separatorLine} />
                
                <View style={[styles.row, styles.totalRow]}>
                  <Text style={styles.totalLabel}>{t.totalCostOverTerm}:</Text>
                  <Text style={styles.totalValue}>{formatCurrency(scenario.totalCostOverTerm || 0)}</Text>
                </View>

                {/* Savings/Extra Costs */}
                {scenario.savingsFromConsolidation && scenario.savingsFromConsolidation > 0 && (
                  <View style={styles.savingsRow}>
                    <View style={styles.row}>
                      <Text style={styles.savingsLabel}>{t.consolidationSavings}:</Text>
                      <Text style={styles.savingsValue}>{formatCurrency(scenario.savingsFromConsolidation)}</Text>
                    </View>
                  </View>
                )}

                {(() => {
                  const savings = calculateSavings(scenario)
                  return savings && savings > 0 ? (
                    <View style={styles.savingsRow}>
                      <View style={styles.row}>
                        <Text style={styles.savingsLabel}>{t.savingsVsBest}:</Text>
                        <Text style={styles.savingsValue}>{formatCurrency(savings)}</Text>
                      </View>
                    </View>
                  ) : null
                })()}
              </View>

              {/* Existing Debts - Condensed */}
              {scenario.debts.length > 0 && (
                <View style={styles.debtSection}>
                  <Text style={styles.debtTitle}>{t.existingDebts}</Text>
                  <Text style={[styles.debtItem, { fontWeight: 'bold' }]}>
                    {scenario.debts.filter(d => d.includeInMortgage).length} {t.consolidated}, {scenario.debts.filter(d => !d.includeInMortgage).length} {t.separate}
                  </Text>
                </View>
              )}
            </View>
          ))}
        </View>

        {/* Footer */}
        <View style={styles.footer}>
          <Text style={{ fontSize: 7, color: '#9ca3af', textAlign: 'center', marginBottom: 3 }}>
            {t.generatedAt} {new Date().toLocaleDateString(language === 'fr' ? 'fr-CA' : 'en-CA')} - MortgageFlow.pro
          </Text>
          <Text style={{ fontSize: 6, color: '#9ca3af', textAlign: 'center', lineHeight: 1.2 }}>
            {t.disclaimer}
          </Text>
        </View>
      </Page>
    </Document>
  )
}

export default MortgageCalculatorPDF
